/**
 * DocPreview.jsx - the generated Word document, rendered in the browser with
 * docx-preview. The library is loaded only when a preview is first shown
 * (dynamic import), so it stays out of the main bundle; it works with or
 * without LibreOffice on the server. Page breaks, headers and footers are
 * close to Word's but not exact - the downloaded file is the real thing.
 */
import { useEffect, useRef, useState } from 'react';
import { MUTED } from './quoteUi';

/**
 * docx-preview draws a floating "behind text" picture as an ordinary inline
 * one, so the full-page dot grid in the header pushed every page's text a
 * page down. Put any page-sized header picture back behind the page.
 */
export function pinPageBackgrounds(root) {
  if (!root) return;
  for (const img of root.querySelectorAll('header img')) {
    const r = img.getBoundingClientRect();
    const w = r.width || img.width;
    const h = r.height || img.height;
    if (!(w >= 700 && h >= 900)) continue;
    const page = img.closest('section');
    if (page) {
      page.style.position = 'relative';
      page.style.isolation = 'isolate';
    }
    Object.assign(img.style, { position: 'absolute', left: '0', top: '0', zIndex: '-1', pointerEvents: 'none' });
  }
}

export default function DocPreview({ blob }) {
  const host = useRef(null);
  const [state, setState] = useState('loading');
  useEffect(() => {
    let live = true;
    if (!blob || !host.current) return undefined;
    setState('loading');
    host.current.innerHTML = '';
    import('docx-preview')
      .then((m) =>
        m.renderAsync(blob, host.current, null, {
          className: 'gops-docx',
          inWrapper: true,
          ignoreLastRenderedPageBreak: true,
          renderHeaders: true,
          renderFooters: true,
        })
      )
      .then(() => {
        if (live) {
          pinPageBackgrounds(host.current);
          setState('ready');
        }
      })
      .catch(() => {
        if (live) setState('failed');
      });
    return () => {
      live = false;
    };
  }, [blob]);
  return (
    <div>
      {state === 'loading' && <div style={{ fontSize: 11.5, color: MUTED, padding: 12 }}>Drawing the preview...</div>}
      {state === 'failed' && (
        <div role="alert" style={{ fontSize: 11.5, color: '#B71C1C', padding: 12 }}>
          The preview could not be drawn. Download the Word file to see it.
        </div>
      )}
      <div ref={host} data-testid="docx-preview" />
    </div>
  );
}
